<script lang="ts" setup>
import GenderBadge from "@/components/MateStyle/GenderBadge.vue";
import UserAvatar from "@/components/UserAvatar.vue";
import i18n from "@/locales";
import { useAdmire } from "@/stores/admire";
import { useFollow } from "@/stores/follow";
import { useGlobalUser } from "@/stores/globalUser";
import { useMate } from "@/stores/mate";
import { MediaUploadFileItem } from "@/types/component";
import { Mate, MateLink } from "@/types/entity";
import { useMateTypeText } from "@/use/common";
import {
  Image,
  showConfirmDialog,
  showImagePreview,
  showLoadingToast,
} from "vant";
import { computed, nextTick, onActivated, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { Media } from "@/types/entity";
import { MateAdoptionStatus } from "@/enums/mateEnums";
import {
  createOneAdoptionFavorite,
  deleteOneAdoptionFavorite,
  transferOneMateAdoption,
} from "@/apis/mate";
import { useI18n } from "vue-i18n";
import { useGlobalModal } from "@/stores/globalModal";

/**
 * 未关注：展示关注按钮 - 关注高亮
 * 已关注：展示关注中、守护按钮 - 守护高亮
 * 已守护：展示守护中、未Ta创作按钮 - 创作高亮
 */
// Props & Emit
export interface MateCardProps {
  showChatBtn?: boolean;
  mate: Mate;
  enableBtnGroup?: boolean;
}

const props: MateCardProps = withDefaults(defineProps<MateCardProps>(), {
  showChatBtn: true,
  enableBtnGroup: true,
});

// 状态
const mateStore = useMate();
const userStore = useGlobalUser();
const router = useRouter();
const route = useRoute();
const mateType = useMateTypeText(props.mate.type);
const bannerUrl = computed(
  () =>
    props.mate?.bannerUrl ??
    props.mate?.mediaUrl ??
    "/img/icon/logo-grayscale.webp"
);
var isMyMate = computed(() => {
  return mateStore.uuidList.includes(props.mate.uuid);
});

const followId = ref<number | null>(null);
const followStore = useFollow();
const admireStore = useAdmire();
const isLoading = ref(false);
const showBannerEditPopup = ref(false);
const creator = computed(() => {
  return isMyMate.value ? mateStore.userMate : props.mate.user.userMate;
});
const defaultSpriteMedia = computed<Media | null | undefined>(() => {
  if (!props.mate.spritezz || props.mate.spritezz.length === 0) return null;
  const defaultIdx = props.mate.spritezz?.findIndex(
    (sprite) => sprite.isDefault
  );
  const idx = defaultIdx > -1 ? defaultIdx : 0;
  return props.mate.spritezz[idx].imageMedia;
});
const videoRef = ref<HTMLVideoElement | null>(null);

const mateLink = ref<MateLink | null>();

const isTreehole = computed(() => {
  return route.query.isTreehole === "1";
});

watch(
  () => route.query.mateLinkId,
  (newVal) => {
    if (newVal) {
      mateLink.value = mateStore.mateLinkList.find(
        (item) => item.id === Number(newVal)
      );
    }
  }
);

// const mateLink = computed(() => {
//   return mateStore.mateLinkList.find((item) => item.id === mateLinkId.value);
// });

const initData = () => {
  if (!props.enableBtnGroup || !userStore.isLogin) return;
  isLoading.value = true;
  const p1 = followStore.isFollowing(props.mate.id!).then((recordId) => {
    followId.value = recordId;
  });

  if (isTreehole.value) {
    mateLink.value = props.mate.mateLink;
  }

  Promise.all([p1]).finally(() => {
    isLoading.value = false;
  });
};

watch(() => admireStore.reloadIdx, initData);

// Lifecycle
onMounted(initData);

onActivated(() => {
  nextTick(() => {
    if (videoRef.value) {
      videoRef.value.play();
    }
  });
});

const onAvatarClick = () => {
  if (!props.mate.mediaUrl) return;
  showImagePreview({
    images: [props.mate.mediaUrl],
    startPosition: 0,
  });
};

const onImageSelected = async (mediaList: MediaUploadFileItem[]) => {
  const loading = showLoadingToast(i18n.global.t("Message.loading"));
  try {
    const media = mediaList[0];
    await mateStore.updateMateDetail(props.mate!.uuid, {
      bannerUrl: media.url,
    });
    showBannerEditPopup.value = false;
  } finally {
    loading.close();
  }
};

const clickCollect = () => {
  if (!userStore.isLogin) return userStore.openLoginModal();
  if (!props.mate.currentAdoption) return;
  if (!props.mate.currentAdoption.isFavorited) {
    createOneAdoptionFavorite(props.mate.currentAdoption.id!).then(() => {
      props.mate.currentAdoption!.isFavorited = true;
    });
  } else {
    deleteOneAdoptionFavorite(props.mate.currentAdoption!.id!).then(() => {
      props.mate.currentAdoption!.isFavorited = false;
    });
  }
};

const { t } = useI18n();
const modalStore = useGlobalModal();
const clickAdopt = async () => {
  if (!userStore.isLogin) return userStore.openLoginModal();
  if (!props.mate.currentAdoption) return;
  if (
    modalStore.shouldOpenPowerNotEnoughModal({
      spentPowerAmount: props.mate.currentAdoption!.powerCubes,
      cb: clickAdopt,
    })
  )
    return;

  try {
    await showConfirmDialog({
      title: t("adoption.confirmAdopt"),
      message: t("adoption.confirmAdoptTips"),
      confirmButtonText: t("Buttons.confirm"),
      cancelButtonText: t("Buttons.cancel"),
    });
  } catch (error) {
    return;
  }
  const loading = showLoadingToast(t("Message.loading"));
  try {
    const res = await transferOneMateAdoption(props.mate.currentAdoption!.id);
    loading.close();
    await mateStore.fetchMateList();
    userStore.refresh();
    showLoadingToast({
      message: t("adoption.adoptSuccess"),
      duration: 3000,
      onClose: () => {
        router.replace({
          name: "mateEdit",
          params: { uuid: res.data.mate.uuid },
        });
      },
    });
  } catch {
    loading.close();
  }
};

const clickEditAdopt = async () => {
  router.push({
    name: "adoptionCreate",
    query: {
      adoptionUuid: props.mate.currentAdoption?.uuid,
    },
  });
};

const clickUnlink = async () => {
  const loading = showLoadingToast(t("Message.loading"));
  await mateStore.deleteMateLink(mateLink.value!.id!);
  mateLink.value = null;
  loading.close();
};

const clickLink = async () => {
  const loading = showLoadingToast(t("Message.loading"));
  const _mateLink = await mateStore.createMateLink(props.mate.id!);
  mateLink.value = _mateLink;
  loading.close();
};
</script>
<template>
  <div
    :class="[
      'w-full flex items-center justify-center gap-3 shadow flex-col overflow-hidden relative',
      isTreehole ? '-mb-4' : '',
    ]"
  >
    <!-- 头像 & 名称 & 类型 -->
    <div class="w-full">
      <div class="relative border-b-4 border-base-200">
        <video
          v-if="bannerUrl.includes('.mp4')"
          ref="videoRef"
          :src="bannerUrl"
          class="w-full h-full flex aspect-[16/9] object-cover"
          crossorigin="anonymous"
          playsinline
          muted
          autoplay
          loop
        />
        <Image
          v-else
          :src="bannerUrl"
          class="w-full h-full flex aspect-[16/9]"
          crossorigin="anonymous"
          fit="cover"
        />
        <button
          v-if="isMyMate"
          class="absolute right-2 top-2 z-10 btn btn-xs btn-square"
          @click="showBannerEditPopup = true"
        >
          <i class="iconify mingcute--edit-3-line" />
        </button>
      </div>
      <div class="flex flex-col w-full gap-2 px-2.5 pb-2">
        <div class="-mt-20 flex justify-between relative z-[1] gap-1 items-end">
          <!-- 上：头像 -->
          <div class="flex gap-1">
            <user-avatar
              :mate="mate"
              class="w-[7.5rem] relative border-4 border-base-200"
              @click="onAvatarClick"
            />
          </div>
          <!-- 形象 & 按钮 -->
          <div
            v-if="mateLink !== undefined"
            class="bg-base-100 flex-1 flex flex-col gap-2 mb-2 px-5 py-3 rounded-lg"
          >
            <div v-if="mateLink?.id" class="flex flex-col gap-2">
              <div class="text-xs">
                <p>
                  {{ $t("module.treehole.connectDays") }}：
                  {{ mateLink?.linkedDays }}
                </p>
                <!-- <p>亲密度： {{ moduleLink.affinityValue }}</p> -->
              </div>

              <button
                class="btn btn-xs w-full btn-outline"
                @click="clickUnlink"
              >
                <i class="iconify mingcute--unlink-line" />
                {{ $t("module.treehole.disconnect") }}
              </button>
            </div>
            <div v-else>
              <p class="text-xs mb-2">
                {{ $t("module.treehole.connectTips") }}
              </p>
              <button class="btn btn-xs w-full btn-outline" @click="clickLink">
                <i class="iconify mingcute--link-line" />
                {{ $t("module.treehole.connect") }}
              </button>
            </div>
          </div>
          <div v-else class="flex gap-2 flex-1">
            <div class="flex items-end">
              <div
                class="rounded border-4 border-base-200"
                :class="{
                  'border-dashed border-base-content/50': !defaultSpriteMedia,
                  'border-base-200': defaultSpriteMedia,
                }"
                @click="
                  isMyMate &&
                    router.push({
                      name: 'sprite',
                      query: { uuid: mate.uuid },
                    })
                "
              >
                <div
                  v-if="defaultSpriteMedia"
                  class="w-14 h-14 flex items-center justify-center"
                >
                  <img
                    :src="defaultSpriteMedia.url"
                    crossorigin="anonymous"
                    class="h-14"
                  />
                </div>
                <div
                  v-else-if="isMyMate"
                  class="w-14 h-14 flex flex-col items-center justify-center text-xs text-center p-2.5 bg-base-300 text-base-content/50 gap-1 cursor-pointer"
                  @click="
                    router.push({
                      name: 'sprite',
                      query: { uuid: mate.uuid },
                    })
                  "
                >
                  <i class="iconify mingcute--add-fill" />
                  {{ $t("characterGenerator.messages.createSprite") }}
                </div>
                <div
                  v-else
                  class="w-14 h-14 flex items-center justify-center text-xs text-center p-2.5 bg-base-300 text-base-content/50"
                >
                  {{ $t("characterGenerator.messages.unsetSprite") }}
                </div>
              </div>
            </div>
            <div class="flex flex-col justify-end flex-1 gap-1">
              <div v-if="isMyMate" class="flex flex-col gap-1 justify-end">
                <RouterLink
                  class="self-end"
                  :to="{ name: 'mateEdit', params: { uuid: mate.uuid } }"
                >
                  <button class="btn btn-primary rounded btn-sm">
                    <i class="iconify mingcute--user-edit-line" />
                    {{ $t("Buttons.editProfile") }}
                  </button>
                </RouterLink>

                <span
                  class="self-end"
                  v-if="
                    mate.adoptionStatus === MateAdoptionStatus.Available &&
                    mate.currentAdoption?.uuid
                  "
                  @click="clickEditAdopt"
                >
                  <button class="btn btn-outline rounded btn-sm">
                    <i class="iconify mingcute--edit-2-line" />
                    编辑领养
                  </button>
                </span>
              </div>
              <div
                class="bg-base-300/80 text-xs p-2 rounded-lg"
                v-else-if="mate.adoptionStatus === MateAdoptionStatus.Available"
              >
                <h1 class="font-semibold text-sm">
                  {{ $t(`adoption.pendingAdoption`) }}
                </h1>

                <p class="text-xs">
                  {{
                    $t(`adoption.manuscriptCount`, {
                      count: mate.mateMediazzCount,
                    })
                  }}
                </p>
                <p class="text-xs">
                  {{
                    $t(
                      `adoption.${
                        mate.currentAdoption?.isAiGenerated ? "hasAi" : "noAi"
                      }`
                    )
                  }}
                </p>
                <div class="flex flex-col gap-2 mt-2">
                  <button
                    v-if="!mate.currentAdoption?.isFavorited"
                    @click="clickCollect"
                    class="btn btn-outline rounded btn-xs gap-1"
                  >
                    <i class="iconify mingcute--heart-line w-4" />
                    {{ $t("adoption.collection") }}
                  </button>
                  <button
                    v-else
                    @click="clickCollect"
                    class="btn btn-primary btn-outline rounded btn-xs gap-1"
                  >
                    <i class="iconify mingcute--heart-line w-4" />
                    {{ $t("adoption.collected") }}
                  </button>
                  <button
                    class="btn btn-primary rounded btn-xs"
                    @click="clickAdopt"
                  >
                    <PowerIcon />
                    {{ mate.currentAdoption?.powerCubes }}
                    {{ $t("adoption.adopt") }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 下：Mate信息 -->
        <div class="w-full flex flex-col gap-2 relative">
          <!-- 名称/性别/类型 -->
          <div class="flex flex-col gap-1">
            <div class="my-2">
              <div class="font-bold text-xl">{{ mate.name }}</div>
            </div>
            <div v-if="!isTreehole" class="flex gap-2 text-base-content/50">
              <div class="flex items-center">
                <gender-badge :gender="mate.gender" class="w-5 -ml-1.5" />
                {{ mateType }}
              </div>
              <template
                v-if="
                  mate.adoptionStatus !== MateAdoptionStatus.Available ||
                  isMyMate
                "
              >
                |
                <div class="flex items-center gap-2">
                  {{ $t("User.creator") }}:
                  <RouterLink
                    :to="{ name: 'mateHome', params: { uuid: creator?.uuid } }"
                    class="flex items-center gap-1 cursor-pointer text-base-content"
                  >
                    <user-avatar class="w-5" is-user :mate="creator" />
                    <span class="text-sm">{{ creator?.name }}</span>
                  </RouterLink>
                </div>
              </template>
            </div>
            <div v-else class="flex flex-col gap-2.5 text-xs">
              <div class="flex items-center flex-wrap gap-1.5" v-if="mate.tag">
                <!-- <div class="text-base-content/50">角色标签：</div> -->
                <div
                  class="badge badge-outline badge-sm"
                  v-for="item in mate.tag.split(',')"
                  :key="item"
                >
                  {{ item }}
                </div>
              </div>

              <div class="flex items-center" v-if="mate.fromWork">
                <span class="text-base-content/50">
                  {{ $t("module.from") }}：
                </span>
                <span>
                  {{ mate.fromWork }}
                </span>
              </div>
            </div>
          </div>
          <!-- 关注/守护count -->
          <div class="flex text-base-content/50" v-if="!isTreehole">
            <div>
              {{ $t("Overview.likes") }}：
              <span>{{ mate.emojiCount || 0 }}</span>
            </div>
          </div>
          <div v-if="mate.bio" class="w-full" v-html="mate.bio" />
        </div>
      </div>
    </div>
    <MutiMediaSelectPopup
      v-model:show="showBannerEditPopup"
      :max-count="1"
      :multiple="false"
      :upload-type="3"
      media-type="video"
      replace
      enable-crop
      :crop-aspect-ratio="16 / 9"
      :allow-crop-outside="false"
      :enable-mate-gallery="false"
      enable-draw
      @onMediaSelected="onImageSelected"
    />
  </div>
</template>
